import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import type {
  CronjobExecutionData,
  CronjobExecutionListItemData,
  CronjobExecutionListQueryData,
  CronjobExecutionStatus,
} from "./types.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import type { Cronjob } from "../Cronjob/index.js";
import { DateTime } from "luxon";
import { User } from "../../user/index.js";

export class CronjobExecution extends ReferenceModel {
  public readonly cronjob: Cronjob;

  public constructor(id: string, cronjob: Cronjob) {
    super(id);
    this.cronjob = cronjob;
  }
  public static ofId(id: string, cronjob: Cronjob) {
    return new CronjobExecution(id, cronjob);
  }

  public static find = provideReact(async (id: string, cronjob: Cronjob) => {
    const data = await config.behaviors.cronjobExecution.find(id, cronjob.id);
    if (data !== undefined) {
      return new CronjobExecutionDetailed(data, cronjob);
    }
  });

  public static get = provideReact(async (id: string, cronjob: Cronjob) => {
    const cronjobExecution = await this.find(id, cronjob);
    assertObjectFound(cronjobExecution, "cronjobExecution", id);
    return cronjobExecution;
  });

  public findDetailed = provideReact(
    () => CronjobExecution.find(this.id, this.cronjob),
    [this.id],
  ) as AsyncResourceVariant<
    () => Promise<CronjobExecutionDetailed | undefined>
  >;

  public getDetailed = provideReact(
    () => CronjobExecution.get(this.id, this.cronjob),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CronjobExecutionDetailed>>;

  public static query(
    cronjob: Cronjob,
    query: CronjobExecutionListQueryData = {},
  ) {
    return new CronjobExecutionListQuery(cronjob, query);
  }
}

class CronjobExecutionCommon extends classes(
  DataModel<CronjobExecutionListItemData | CronjobExecutionData>,
  CronjobExecution,
) {
  public readonly status: CronjobExecutionStatus;
  public readonly isRunning: boolean;
  public readonly start?: DateTime;
  public readonly successful: boolean;
  public readonly durationInSeconds?: number;
  public readonly triggeredBy?: User;
  public readonly logPath?: string;

  public constructor(
    data: CronjobExecutionListItemData | CronjobExecutionData,
    cronjob: Cronjob,
  ) {
    super([data], [data.id, cronjob]);
    this.status = data.status;
    this.isRunning = data.status === "Running" || data.status === "Pending";
    this.start = data.start ? DateTime.fromISO(data.start) : undefined;
    this.successful = data.successful;
    this.durationInSeconds = data.durationInMilliseconds
      ? Math.floor(data.durationInMilliseconds / 1000)
      : undefined;
    this.triggeredBy = data.triggeredBy?.id
      ? User.ofId(data.triggeredBy.id)
      : undefined;
    this.logPath = data.logPath;
  }

  public findLog = provideReact(
    async (projectId: string) => {
      if (!this.logPath) {
        return;
      }
      return config.behaviors.cronjobExecution.findLog(projectId, this.logPath);
    },
    [this.id],
  );
}

export class CronjobExecutionDetailed extends classes(
  CronjobExecutionCommon,
  DataModel<CronjobExecutionData>,
) {
  public constructor(data: CronjobExecutionData, cronjob: Cronjob) {
    super([data, cronjob], [data]);
  }
}

export class CronjobExecutionListItem extends classes(
  CronjobExecutionCommon,
  DataModel<CronjobExecutionListItemData>,
) {
  public constructor(data: CronjobExecutionListItemData, cronjob: Cronjob) {
    super([data, cronjob], [data]);
  }
}

export class CronjobExecutionListQuery extends ListQueryModel<CronjobExecutionListQueryData> {
  public readonly cronjob: Cronjob;

  public constructor(
    cronjob: Cronjob,
    query: CronjobExecutionListQueryData = {},
  ) {
    super(query, {
      dependencies: [cronjob.id],
    });
    this.cronjob = cronjob;
  }

  public refine(query: CronjobExecutionListQueryData) {
    return new CronjobExecutionListQuery(this.cronjob, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.cronjobExecution.list(
      this.cronjob.id,
      this.query,
    );
    return new CronjobExecutionList(
      this.cronjob,
      this.query,
      items.map((d) => new CronjobExecutionListItem(d, this.cronjob)),
      totalCount,
    );
  }, [this.queryId, "cronjobExecution"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "cronjobExecution"]);
}

export class CronjobExecutionList extends classes(
  CronjobExecutionListQuery,
  ListDataModel<CronjobExecutionListItem>,
) {
  public constructor(
    cronjob: Cronjob,
    query: CronjobExecutionListQueryData,
    cronjobExecutions: CronjobExecutionListItem[],
    totalCount: number,
  ) {
    super([cronjob, query], [cronjobExecutions, totalCount]);
  }
}
